import { useEffect, useRef, useState } from 'react';

/*
  Scroll entry for landing sections.

  One IntersectionObserver per section, disconnected as soon as the section has
  been seen. Deliberately not a scroll listener: this runs off the main
  scrolling path and never re-renders while the user is scrolling.

  Under `prefers-reduced-motion: reduce` the content is marked visible up front
  so nothing depends on an animation that will not run.
*/
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function useReveal(options = {}) {
  const { threshold = 0.15, rootMargin = '0px 0px -8% 0px' } = options;
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;

    if (!node || typeof IntersectionObserver === 'undefined' || prefersReducedMotion()) {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return [ref, isVisible];
}

/* Convenience for `className` composition in the landing components. */
export const reveal = (isVisible, extra = '') =>
  `ab-reveal${isVisible ? ' is-visible' : ''}${extra ? ` ${extra}` : ''}`;
